import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Columns3, GripVertical, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableRow({ col, visible, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 border border-transparent hover:border-slate-200"
      data-testid={`col-row-${col.key}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-slate-400 hover:text-slate-600 touch-none"
        aria-label="Trascina per riordinare"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        checked={visible}
        onCheckedChange={() => onToggle(col.key)}
        id={`col-cb-${col.key}`}
        data-testid={`col-cb-${col.key}`}
      />
      <label htmlFor={`col-cb-${col.key}`} className="text-sm text-slate-700 cursor-pointer flex-1 select-none">
        {col.label}
      </label>
    </div>
  );
}

/**
 * Sprint 2B.2-A — Picker raggruppato per categorie con accordion.
 *
 * Props:
 *  - allColumns: [{key, label}]  (ordine canonico, NON quello dell'utente)
 *  - order: string[]   ordine corrente delle colonne (override utente, applicato in tabella)
 *  - visible: string[] subset visibile
 *  - categories?: [{name, keys:[]}]  raggruppamenti per accordion. Se assente, mostra flat.
 *  - onChange({order, visible})
 *  - onReset()
 */
export default function VehicleColumnsButton({ allColumns, order, visible, categories, onChange, onReset, onResetWidths }) {
  const [open, setOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const knownKeys = new Set(allColumns.map((c) => c.key));
  const cleanOrder = (order || []).filter((k) => knownKeys.has(k));
  for (const c of allColumns) if (!cleanOrder.includes(c.key)) cleanOrder.push(c.key);

  const colsByKey = Object.fromEntries(allColumns.map((c) => [c.key, c]));

  const [openCats, setOpenCats] = useState({});
  const toggleCat = (name) => setOpenCats((s) => ({ ...s, [name]: !s[name] }));

  // Sprint LISTE-2 — un solo ordine: le colonne accese, nell'ordine reale della tabella, si trascinano
  // in qualsiasi posizione. Le spente conservano il loro posto: riaccese tornano dov'erano.
  const ordineVisibili = cleanOrder.filter((k) => visible.includes(k));
  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const newOrder = arrayMove(cleanOrder, cleanOrder.indexOf(active.id), cleanOrder.indexOf(over.id));
    onChange({ order: newOrder, visible });
  };

  const toggleVisible = (key) => {
    const next = visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key];
    onChange({ order: cleanOrder, visible: next });
  };

  const renderOrdine = () => (
    <div className="mb-2" data-testid="col-ordine-tabella">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide font-medium text-slate-500">Ordine in tabella</div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ordineVisibili} strategy={verticalListSortingStrategy}>
          <div className="space-y-0.5">
            {ordineVisibili.map((k) => colsByKey[k] && (
              <SortableRow key={k} col={colsByKey[k]} visible onToggle={toggleVisible} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );

  const renderSezioni = () => (
    <div className="space-y-1 pt-2 border-t border-slate-200" data-testid="col-sezioni">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide font-medium text-slate-500">Colonne disponibili</div>
      {(categories && categories.length ? categories : [{ name: "Tutte", keys: allColumns.map((c) => c.key) }]).map((cat) => {
        const keys = cat.keys.filter((k) => knownKeys.has(k));
        if (keys.length === 0) return null;
        const expanded = !!openCats[cat.name];
        return (
          <div key={cat.name} className="border border-slate-100 rounded">
            <button type="button" onClick={() => toggleCat(cat.name)}
              className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-slate-50"
              data-testid={`col-cat-${cat.name}`}>
              <span className="text-xs font-medium uppercase tracking-wide text-slate-600 flex items-center gap-1">
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {cat.name}
              </span>
              <span className="text-[10px] text-slate-400">{keys.filter((k) => visible.includes(k)).length}/{keys.length}</span>
            </button>
            {expanded && (
              <div className="px-1 pb-1 pt-0.5">
                {keys.map((k) => (
                  <div key={k} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50" data-testid={`col-scelta-${k}`}>
                    <Checkbox checked={visible.includes(k)} onCheckedChange={() => toggleVisible(k)}
                      id={`col-sc-${k}`} data-testid={`col-sc-${k}`} />
                    <label htmlFor={`col-sc-${k}`} className="text-sm text-slate-700 cursor-pointer flex-1 select-none">{colsByKey[k].label}</label>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="columns-btn">
          <Columns3 className="h-4 w-4 mr-1.5" /> Colonne
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2 max-h-[75vh] overflow-y-auto" align="end">
        <div className="flex items-center justify-between px-2 pb-2 border-b border-slate-200 mb-1">
          <div className="text-xs uppercase font-medium text-slate-500 tracking-wide">Colonne</div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-7 text-xs text-slate-500 hover:text-slate-700"
            data-testid="columns-reset-btn"
          >
            <RotateCcw className="h-3 w-3 mr-1" /> Default
          </Button>
          {onResetWidths && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetWidths}
              className="h-7 text-xs text-slate-500 hover:text-slate-700"
              data-testid="columns-reset-widths-btn"
            >
              Reset larghezze
            </Button>
          )}
        </div>
        {renderOrdine()}
        {renderSezioni()}
      </PopoverContent>
    </Popover>
  );
}
